'use client';

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  BookmarksSimpleIcon,
  CaretDoubleDownIcon,
  CaretDoubleUpIcon,
  CheckIcon,
  FileCloudIcon,
  ListPlusIcon,
  PaperclipIcon,
} from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { TpTooltip } from '@tranquility-ai/theme-components/tooltip';
import { isDisplayableMediaType } from '@/shared/constants';
import { getMediaType } from '@/shared/functions';
import type { MboxAttachment, MediaSource, MediaType, SourceContext } from '@/shared/types';
import { urlsLikelySameAttachment } from '@/shared/email-citation';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { MediaDisplay } from './ui/mediaDisplay';

const DOCUMENT_PAGE_ASPECT = 8.5 / 11;
const MEDIA_TOOLBAR_CHROME_PX = 48;

function aspectRatioForSourceMedia(mediaType: MediaType | undefined | null): number {
  switch (mediaType) {
    case 'pdf':
    case 'docx':
    case 'pptx':
      return DOCUMENT_PAGE_ASPECT;
    default:
      return 1;
  }
}

function fitHeightForMediaWidth(
  width: number,
  mediaType: MediaType | undefined | null,
): number {
  if (!(width > 0)) return 0;
  const aspect = aspectRatioForSourceMedia(mediaType);
  return Math.max(1, Math.round(width / aspect) + MEDIA_TOOLBAR_CHROME_PX);
}

export type EmailAttachmentsListProps = {
  attachments: MboxAttachment[];
  caseId?: string | null;
  jsonMediaDisplayEnabled?: boolean;
  /** Open this row when attachments first load (cited attachment in chat). */
  defaultOpenIndex?: number | null;
  /** Forward citation context onto the cited attachment's MediaDisplay (transcripts). */
  citedAnalysisFilePath?: string;
  citedSourceContext?: SourceContext;
  onCopyPath?: (attachment: MboxAttachment, index: number) => void;
  onAddToFavorites?: (attachment: MboxAttachment, index: number) => void;
  onCreateEvent?: (attachment: MboxAttachment, index: number) => void;
  copiedPathIndex?: number | null;
  addedToFavoritesIndex?: number | null;
  createdEventIndex?: number | null;
};

export function EmailAttachmentsList({
  attachments,
  caseId,
  jsonMediaDisplayEnabled = false,
  defaultOpenIndex = null,
  citedAnalysisFilePath,
  citedSourceContext,
  onCopyPath,
  onAddToFavorites,
  onCreateEvent,
  copiedPathIndex = null,
  addedToFavoritesIndex = null,
  createdEventIndex = null,
}: EmailAttachmentsListProps) {
  const t = useTimePilotT();
  const [openAttachmentIndex, setOpenAttachmentIndex] = useState<number | null>(defaultOpenIndex);
  const attachmentMediaSlotRef = useRef<HTMLDivElement>(null);
  const [attachmentSlotWidth, setAttachmentSlotWidth] = useState(0);

  const attachmentsKey = attachments.map((a) => a.attachmentUrl).join('|');
  useEffect(() => {
    setOpenAttachmentIndex(defaultOpenIndex ?? null);
  }, [attachmentsKey, defaultOpenIndex]);

  const openAttachment = useMemo(() => {
    if (openAttachmentIndex == null) return null;
    return attachments.find((a) => a.attachmentIndex === openAttachmentIndex) ?? null;
  }, [openAttachmentIndex, attachments]);

  const openAttachmentMediaType = useMemo((): MediaType => {
    if (!openAttachment?.attachmentUrl) return 'unknown';
    return getMediaType(
      { mediaType: 'unknown', mediaUrl: openAttachment.attachmentUrl } as MediaSource,
      null,
    );
  }, [openAttachment]);

  useLayoutEffect(() => {
    if (openAttachmentIndex == null) {
      setAttachmentSlotWidth(0);
      return;
    }
    const el = attachmentMediaSlotRef.current;
    if (!el) return;
    const sync = () => {
      const width = Math.floor(el.getBoundingClientRect().width);
      if (width <= 0) return;
      setAttachmentSlotWidth((prev) => (prev === width ? prev : width));
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, [openAttachmentIndex, attachments.length]);

  const attachmentFitWidth =
    openAttachmentIndex != null && attachmentSlotWidth > 0 ? attachmentSlotWidth : undefined;
  const openAttachmentIsContinuousMedia =
    openAttachmentMediaType === 'video' || openAttachmentMediaType === 'audio';
  const attachmentFitHeight =
    attachmentFitWidth != null && !openAttachmentIsContinuousMedia
      ? fitHeightForMediaWidth(attachmentFitWidth, openAttachmentMediaType)
      : undefined;

  if (!attachments.length) return null;

  return (
    <div className="flex flex-col gap-1" data-testid="email-attachments">
      <div className="text-white font-normal text-base pb-2 select-none" />
      <div className="flex flex-row items-center gap-2 text-white font-normal text-base px-2 py-1 select-none">
        <PaperclipIcon weight="duotone" className="w-6 h-6" />
        {attachments.length} {attachments.length === 1 ? t('sources.attachment') : t('sources.attachments')}
      </div>
      {attachments.map((attachment, index) => (
        <div
          key={attachment.attachmentUrl || index}
          className="box-border flex flex-col gap-2 pb-2 border-[1px] border-dashed border-white/5 rounded-md p-2 hover:bg-white/5"
        >
          <div className="flex flex-row items-center justify-between px-2 pt-1">
            <div className="flex min-w-0 flex-row items-center gap-2">
              <Button
                iconOnly
                size="sm"
                style={openAttachmentIndex === attachment.attachmentIndex ? 'solid' : 'text'}
                color={openAttachmentIndex === attachment.attachmentIndex ? 'accent' : 'light'}
                background="inverse"
                className="px-2 py-1 select-none"
                aria-label={
                  openAttachmentIndex === attachment.attachmentIndex
                    ? t('sources.collapseAttachment')
                    : t('sources.expandAttachment')
                }
                onClick={() => {
                  setOpenAttachmentIndex((prev) =>
                    prev === attachment.attachmentIndex ? null : attachment.attachmentIndex,
                  );
                }}
              >
                {openAttachmentIndex === attachment.attachmentIndex ? (
                  <CaretDoubleUpIcon />
                ) : (
                  <CaretDoubleDownIcon />
                )}
              </Button>
              <div className="min-w-0 truncate text-white font-normal text-base">
                {attachment.fileName}
              </div>
            </div>
            <div className="flex flex-row items-center gap-1">
              {onCopyPath && (
                <TpTooltip content={t('sources.copyAttachment')} placement="bottom" delayDuration={0} sideOffset={2}>
                  <Button
                    iconOnly
                    size="sm"
                    style={copiedPathIndex === attachment.attachmentIndex ? 'solid' : 'text'}
                    color={copiedPathIndex === attachment.attachmentIndex ? 'accent' : 'light'}
                    background="inverse"
                    aria-label={t('sources.copyAttachment')}
                    onClick={() => onCopyPath(attachment, attachment.attachmentIndex)}
                    onTouchEnd={() => onCopyPath(attachment, attachment.attachmentIndex)}
                  >
                    {copiedPathIndex === attachment.attachmentIndex ? <CheckIcon /> : <FileCloudIcon />}
                  </Button>
                </TpTooltip>
              )}
              {onAddToFavorites &&
                isDisplayableMediaType(
                  getMediaType(
                    { mediaType: 'unknown', mediaUrl: attachment.attachmentUrl } as MediaSource,
                    null,
                  ),
                  { jsonMediaDisplay: jsonMediaDisplayEnabled },
                ) && (
                  <TpTooltip
                    content={addedToFavoritesIndex === attachment.attachmentIndex ? t('sources.added') : t('sources.addToFavorites')}
                    placement="bottom"
                    delayDuration={0}
                    sideOffset={2}
                  >
                    <Button
                      iconOnly
                      size="sm"
                      style={addedToFavoritesIndex === attachment.attachmentIndex ? 'solid' : 'text'}
                      color={addedToFavoritesIndex === attachment.attachmentIndex ? 'accent' : 'light'}
                      background="inverse"
                      onClick={() =>
                        onAddToFavorites(attachment, attachment.attachmentIndex)
                      }
                      onTouchEnd={() =>
                        onAddToFavorites(attachment, attachment.attachmentIndex)
                      }
                      aria-label={
                        addedToFavoritesIndex === attachment.attachmentIndex
                          ? t('sources.added')
                          : t('sources.addToFavorites')
                      }
                    >
                      {addedToFavoritesIndex === attachment.attachmentIndex ? (
                        <CheckIcon />
                      ) : (
                        <BookmarksSimpleIcon weight="duotone" />
                      )}
                    </Button>
                  </TpTooltip>
                )}
              {onCreateEvent && (
                <TpTooltip
                  content={t('sources.createFromAttachment')}
                  placement="bottom"
                  delayDuration={0}
                  sideOffset={2}
                >
                  <Button
                    iconOnly
                    size="sm"
                    style={createdEventIndex === attachment.attachmentIndex ? 'solid' : 'text'}
                    color={createdEventIndex === attachment.attachmentIndex ? 'accent' : 'light'}
                    background="inverse"
                    aria-label={
                      createdEventIndex === attachment.attachmentIndex
                        ? t('sources.eventFromAttachment')
                        : t('sources.createFromAttachment')
                    }
                    onClick={() => onCreateEvent(attachment, attachment.attachmentIndex)}
                    onTouchEnd={() => onCreateEvent(attachment, attachment.attachmentIndex)}
                  >
                    {createdEventIndex === attachment.attachmentIndex ? (
                      <CheckIcon />
                    ) : (
                      <ListPlusIcon />
                    )}
                  </Button>
                </TpTooltip>
              )}
            </div>
          </div>
          {openAttachmentIndex === attachment.attachmentIndex && (
            <div
              ref={attachmentMediaSlotRef}
              className="box-border w-full min-w-0 max-w-full overflow-hidden"
              style={
                attachmentFitWidth && attachmentFitHeight
                  ? {
                      width: '100%',
                      height: attachmentFitHeight,
                      minHeight: attachmentFitHeight,
                    }
                  : { width: '100%' }
              }
            >
              <MediaDisplay
                key={attachment.attachmentUrl}
                isPresentation={false}
                isSourceCard={true}
                caseId={caseId}
                hideMetaSocialViewerButton={true}
                containerWidth={attachmentFitWidth}
                containerHeight={attachmentFitHeight}
                mediaItem={{
                  mediaType: getMediaType(
                    { mediaUrl: attachment.attachmentUrl, mediaType: 'unknown' },
                    null,
                  ),
                  mediaUrl: attachment.attachmentUrl,
                  ...(urlsLikelySameAttachment(attachment.attachmentUrl, citedAnalysisFilePath)
                    ? { mediaContext: { ...(citedSourceContext ?? {}) } }
                    : {}),
                } as MediaSource}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
